import { ChangeDetectionStrategy, Component, computed, input, InputSignal, output } from '@angular/core';

/** Visual tone of the chip; colors are resolved via brand CSS custom properties. */
export type AraGenericChipTone = 'neutral' | 'primary';

/**
 * 'soft' = tonale Fläche (Default); 'outline' = Surface + Divider-Border (Pill-Look für
 * Karten/Formulare); 'dashed' = gestrichelter Rahmen, transparente Fläche — Konvention für
 * "dieser Chip referenziert eine fachliche Entität" (z. B. eine BusinessQuantity), nicht für
 * eine feste Einstufung.
 *
 * 'add' = die Hinzufügen-Affordanz: sieht wie 'dashed' aus, bringt automatisch das Plus-Zeichen
 * mit und rendert immer als Button (Hinzufügen ist eine Aktion). Damit sieht "etwas hinzufügen"
 * überall gleich aus, statt je Tabelle als eigenes SCSS nachgebaut zu werden.
 */
export type AraGenericChipAppearance = 'soft' | 'outline' | 'dashed' | 'add';

/**
 * Generic, reusable display chip. Pass either `tone` for brand-token colors, or
 * `color` (any CSS color, e.g. '#c2410c') for a fully custom shade.
 *
 * Beyond the plain label the chip is the shared shell for every "chip with X" spot:
 * - `removable` + `(removed)` renders a small remove-X INSIDE the pill (a plain button —
 *   never mat-icon-button, whose 40 px touch target breaks out of the 24 px chip).
 * - Projected leading content (default `<ng-content>`) hosts e.g. an `ara-person-chip`
 *   avatar — the worker chip stays its own domain component and just docks in.
 *
 * `removable`/projection only apply to the non-clickable variant — nesting the X inside a
 * clickable chip would produce invalid HTML (button in button).
 */
@Component({
	selector: 'ara-generic-chip',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './ara-generic-chip.component.html',
	styleUrl: './ara-generic-chip.component.scss',
	host: { '[style.--chip-color]': 'color()' },
})
export class AraGenericChipComponent {
	/** Text shown inside the chip. */
	readonly label: InputSignal<string> = input<string>('');
	/** Brand-token color variant. Ignored when `color` is set. */
	readonly tone: InputSignal<AraGenericChipTone> = input<AraGenericChipTone>('neutral');
	/** Any CSS color (hex, rgb, hsl). When set, overrides `tone`. */
	readonly color = input<string | null>(null);
	/** 'outline' renders on surface with divider border instead of the tonal fill. */
	readonly appearance = input<AraGenericChipAppearance>('soft');
	/** Highlight ring for cross-referencing (e.g. hover on a matching value elsewhere). */
	readonly highlighted = input<boolean>(false);
	/** When true, the chip renders as a button and emits `chipClick` when activated. */
	readonly isClickable: InputSignal<boolean> = input<boolean>(false);
	/** Emitted when a clickable chip is activated (click / keyboard). */
	readonly chipClick = output<void>();
	/** Disables the button variant (e.g. an add-chip in a locked row). Ignored when not clickable. */
	readonly disabled = input<boolean>(false);
	/** Shows a small remove-X inside the chip (non-clickable variant only). */
	readonly removable = input<boolean>(false);
	/** Übersetztes aria-label für den Entfernen-Button — vom Aufrufer via `t('…')` übergeben. */
	readonly removeLabel = input<string>('');
	/** Emitted when the remove-X is activated. */
	readonly removed = output<void>();

	/** `appearance="add"` is an action by definition, so it renders as a button without opting in. */
	protected readonly renderAsButton = computed(() => this.isClickable() || this.appearance() === 'add');
}
